import { ApiError } from './errors'

/**
 * Codes in, sentences out — the only place that does this.
 *
 * The server sends `{ field, issue }` rather than prose on purpose: a Pydantic
 * message quotes the value that failed validation, and the value here is
 * patient data. That is the right call, but it means nothing on the wire is fit
 * to show anyone, and the forms were rendering the raw code. Registering a
 * patient with no OP number put the word "string_too_short" under the field.
 *
 * Two rules for the wording below:
 *  - The label is already above the field, so the message does not repeat it.
 *    "This cannot be left empty." reads correctly under any of them.
 *  - Say what to do, not what was violated. "Enter a date as dd/mm/yyyy" beats
 *    "date_from_datetime_parsing" and also beats "Invalid date".
 */

/** Where the generic line would be vague, wrong, or miss the actual rule. */
const BY_FIELD: Record<string, string> = {
  'date_of_birth:required_one_of': 'Enter a date of birth, or an age instead.',
  'date_of_birth:future': 'A date of birth cannot be in the future.',
  'call_date:future': 'A call cannot be logged against a future date.',
  'follow_up_date:before_call': 'A follow-up cannot be before the call itself.',
  'return_at:before_departure': 'The return time cannot be before the departure time.',
  'to:before_from': 'The end date cannot be before the start date.',
  'to:range_too_wide': 'Choose a shorter date range.',
  'status:convert_only': 'Use "Convert to patient" to mark an enquiry as a patient.',
  'q:too_short': 'Type at least two characters to search.',
  'content_type:not_allowed': 'That kind of file cannot be attached.',
  'byte_size:too_large': 'That file is too large to attach.',
  'code:invalid': 'That code is not correct. Check the app and try again.',
  // min_length is 6 here, so "too short" genuinely means short, not empty.
  'code:string_too_short': 'Enter the whole code from the app.',
  'password:string_too_short': 'That password is too short. Use at least 12 characters.',
  'age_years:greater_than_equal': 'An age cannot be negative.',
  'age_years:less_than_equal': 'Enter an age of 130 or less.',
  'op_number:string_too_short': 'Enter the OP number the clinic uses for them.',
  'full_name:string_too_short': 'Enter their name.',
}

/**
 * Everything else, by code alone — the app's own issue codes and Pydantic's
 * error types side by side, because both reach the browser the same way.
 */
const BY_ISSUE: Record<string, string> = {
  required: 'This cannot be left empty.',
  missing: 'This cannot be left empty.',
  string_too_short: 'This cannot be left empty.',
  too_short: 'This is too short.',
  string_too_long: 'This is too long — shorten it.',
  too_long: 'There are too many of these to save at once.',
  too_large: 'This is too large.',
  future: 'This cannot be in the future.',
  before_call: 'This cannot be before the call date.',
  before_from: 'This cannot be before the start date.',
  before_departure: 'This cannot be before the departure time.',
  range_too_wide: 'Choose a shorter date range.',
  required_one_of: 'Fill in this, or the field beside it.',
  not_allowed: 'That is not something this field accepts.',
  invalid: 'That does not look right. Check it and try again.',
  greater_than_equal: 'That number is too small.',
  greater_than: 'That number is too small.',
  less_than_equal: 'That number is too large.',
  int_parsing: 'Enter a number.',
  date_parsing: 'Enter a date as dd/mm/yyyy.',
  date_from_datetime_parsing: 'Enter a date as dd/mm/yyyy.',
  time_parsing: 'Enter a time as hh:mm.',
  uuid_parsing: 'That does not look right. Check it and try again.',
  bool_parsing: 'That does not look right. Check it and try again.',
  enum: 'Choose one of the options offered.',
  literal_error: 'Choose one of the options offered.',
  string_pattern_mismatch: 'That does not look right. Check it and try again.',
  value_error: 'That does not look right. Check it and try again.',
}

/** Last resort. Never shows a code, never blames the person reading it. */
const UNKNOWN_FIELD = 'Check this and try again.'
const UNKNOWN_ERROR = 'Something went wrong. Nothing you typed has been lost.'

export function fieldMessage(field: string, issue: string | undefined): string {
  if (!issue) return UNKNOWN_FIELD
  return BY_FIELD[`${field}:${issue}`] ?? BY_ISSUE[issue] ?? UNKNOWN_FIELD
}

/** Every per-field problem in an error, ready to hand straight to a `<Field>`. */
export function fieldMessages(error: ApiError): Record<string, string> {
  return Object.fromEntries(
    error.details.filter((d) => d.field).map((d) => [d.field!, fieldMessage(d.field!, d.issue)]),
  )
}

/**
 * The banner line for any thrown thing at all.
 *
 * The server's own messages are already written for a person, so most pass
 * through untouched. Only the two it cannot phrase well — a schema rejection it
 * has no field names for, and a crash — are replaced here.
 */
export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return UNKNOWN_ERROR

  if (error.status >= 500 || error.code === 'internal_error') {
    // Quoting a reference is only worth asking for if it is on the screen.
    const reference = error.correlationId?.slice(0, 8)
    return reference
      ? `Something went wrong at our end. Nothing you typed has been lost. Quote reference ${reference} if you report it.`
      : UNKNOWN_ERROR
  }

  return error.message || UNKNOWN_ERROR
}
